import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from './TourContext';
import TourTooltip from './TourTooltip';

const TourOverlay = () => {
  const { isTourActive, currentTourStep } = useTour();
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!isTourActive || !currentTourStep) {
      setTargetRect(null);
      return;
    }

    const findTarget = () => {
      let target = document.querySelector(currentTourStep.target);
      
      // Try fallback target if primary not found
      if (!target && currentTourStep.fallbackTarget) {
        target = document.querySelector(currentTourStep.fallbackTarget);
      }

      if (target) {
        const rect = target.getBoundingClientRect();
        const padding = currentTourStep.spotlightPadding ?? 8;
        
        setTargetRect({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
          element: target
        });

        // Calculate tooltip position based on placement
        calculateTooltipPosition(rect, currentTourStep.placement);
        
        // Scroll target into view if needed
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        // No target found, center the tooltip
        setTargetRect(null);
        setTooltipPosition({
          top: window.innerHeight / 2 - 100,
          left: window.innerWidth / 2 - 175
        });
      }
    };

    // Initial find
    findTarget();

    // Re-find on resize/scroll
    const handleReposition = () => {
      requestAnimationFrame(findTarget);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isTourActive, currentTourStep]);

  const calculateTooltipPosition = (rect, placement) => {
    const tooltipWidth = 350;
    const tooltipHeight = 180;
    const offset = 16;
    const viewportPadding = 16;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + offset;
        // Check if tooltip goes off right edge
        if (left + tooltipWidth > window.innerWidth - viewportPadding) {
          // Move to left side
          left = rect.left - tooltipWidth - offset;
        }
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - offset;
        // Check if tooltip goes off left edge
        if (left < viewportPadding) {
          // Move to right side
          left = rect.right + offset;
        }
        break;
      case 'bottom':
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = rect.top - tooltipHeight - offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'center':
      default:
        top = window.innerHeight / 2 - tooltipHeight / 2;
        left = window.innerWidth / 2 - tooltipWidth / 2;
        break;
    }

    // Ensure tooltip stays within viewport
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipHeight - viewportPadding));
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipWidth - viewportPadding));

    setTooltipPosition({ top, left });
  };

  if (!isTourActive) return null;

  const showOverlay = !currentTourStep?.disableOverlay;

  return createPortal(
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-[9999] pointer-events-none"
      data-testid="tour-overlay"
    >
      {/* Dark overlay with spotlight cutout */}
      {showOverlay && (
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-auto"
          style={{ cursor: 'default' }}
        >
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left}
                  y={targetRect.top}
                  width={targetRect.width}
                  height={targetRect.height}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      )}

      {/* Spotlight border/glow effect */}
      {targetRect && showOverlay && (
        <div
          className="absolute border-2 border-[#ffd108] rounded-lg pointer-events-none"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 4px rgba(255, 209, 8, 0.3), 0 0 20px rgba(255, 209, 8, 0.4)'
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="absolute pointer-events-auto"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transition: 'top 0.3s ease, left 0.3s ease'
        }}
      >
        <TourTooltip />
      </div>
    </div>,
    document.body
  );
};

export default TourOverlay;
